import { defineChatPlace, type ChatPlace } from '../../chat/panel';

/**
 * The chat docked beside a dashboard of pinned results.
 *
 * Two sets of openers, because the chat is reached two ways. From the toolbar
 * nothing is selected, so questions about "this widget" refer to nothing —
 * they ask about the dashboard as a whole. From a widget's wand, that widget
 * is the subject, and the openers say so.
 *
 * Both sets stay inside what the chat can actually answer. It holds a snapshot
 * of every widget, not their history, so "which widget changed the most?"
 * would read well and answer badly.
 */
export const dashboardChat = defineChatPlace({
  id: 'dashboard',
  place: 'docked',
  results: 'inline',
  features: {
    sqlApproval: true,
    voiceCalls: false,
    // Pins straight onto the dashboard this chat is docked to — no picker,
    // because the question "which dashboard?" is already answered by the one
    // you are looking at.
    addToDashboard: true,
    copy: true,
    contextChips: true,
  },
  welcome: { title: '', subtitle: 'Ask about this dashboard.' },
  starters: [
    'Summarise what this dashboard shows',
    'What stands out across these widgets?',
    'Do any of these figures disagree with each other?',
    'What should I look into next?',
  ],
  placeholder: 'Ask about this dashboard…',
});

/**
 * The same chat once widgets have been added to it.
 *
 * Named rather than generic: "Summarise what this shows" beside a chip saying
 * which widget is clearer than repeating the title in every opener, and it
 * survives a long title without wrapping the whole list.
 */
export function dashboardChatForWidgets(titles: string[]): ChatPlace {
  if (titles.length === 0) return dashboardChat;
  const one = titles.length === 1;
  const subject = one ? `“${titles[0]}”` : `${titles.length} widgets`;

  return {
    ...dashboardChat,
    welcome: { title: '', subtitle: `Ask about ${subject}.` },
    starters: [
      one ? 'Summarise what this shows' : 'Summarise what these show',
      'What stands out?',
      one ? 'Why might this be happening?' : 'What do these have in common?',
      'How does this compare with the rest of the dashboard?',
    ],
    placeholder: one ? 'Ask about this widget…' : 'Ask about these widgets…',
  };
}
